<script setup>
import { useCommonCodeStore } from '@/store/commoncode-store';
import { useReservationsStore } from '@/store/reservation-store';
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
    contractType: {
        type: String,
        default: 'SHORT'
    },
    validMsg: {
        type: Object,
        default: () => ({})
    },
    requiredList: {
        type: [Array, Object],
        default: () => []
    },
    readonlyList: {
        type: [Array, Object],
        default: () => []
    },
    hideList: {
        type: [Array, Object],
        default: () => []
    },
    vehicleData: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update', 'update:rentalMonth']);
const route = useRoute();
const store = useReservationsStore();
const sessionKey = `${route.name}`;
const today = new Date();
const tomorrow = new Date(today);
tomorrow.setDate(tomorrow.getDate() + 1);
const formatToYyyyMmDd = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
let formattedDate = formatToYyyyMmDd(today);
let formattedTomorrow = formatToYyyyMmDd(tomorrow);
let currentTime = new Date().toTimeString().slice(0, 5);
const rentalDuration = ref('');
const rentalMonth = ref(0);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};

const selectedDate = computed(() => ({
    pickupDate: data.pickup_date,
    pickupTime: data.pickup_time,
    returnExpectedDate: data.return_expected_date,
    returnExpectedTime: data.return_expected_time
}));

const _toInt = (s) => Number.parseInt(s, 10);

const ymdFromAny = (v) => {
    if (v instanceof Date) return { y: v.getFullYear(), m: v.getMonth() + 1, d: v.getDate(), dash: true };
    if (typeof v === 'number') {
        const dt = new Date(v);
        return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate(), dash: true };
    }
    if (typeof v !== 'string') return null;
    const s = v.trim();
    if (!s) return null;

    if (s.includes('-')) {
        const [y, m, d] = s.split('-');
        const yi = _toInt(y),
            mi = _toInt(m),
            di = _toInt(d);
        if (!yi || !mi || !di) return null;
        return { y: yi, m: mi, d: di, dash: true };
    } else {
        if (!/^\d{8}$/.test(s)) return null;
        const yi = _toInt(s.slice(0, 4));
        const mi = _toInt(s.slice(4, 6));
        const di = _toInt(s.slice(6, 8));
        if (!yi || !mi || !di) return null;
        return { y: yi, m: mi, d: di, dash: false };
    }
};

const dateFromYmd = ({ y, m, d }) => {
    const dt = new Date(y, m - 1, d);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
};

const fmtYMD = (dateObj, dash = true) => {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return dash ? `${y}-${m}-${d}` : `${y}${m}${d}`;
};

const addCalendarMonths = (dateObj, months) => {
    const targetMonth = dateObj.getMonth() + months;
    const targetLastDay = new Date(dateObj.getFullYear(), targetMonth + 1, 0).getDate();
    const targetDay = Math.min(dateObj.getDate(), targetLastDay);

    return new Date(dateObj.getFullYear(), targetMonth, targetDay, dateObj.getHours(), dateObj.getMinutes(), dateObj.getSeconds(), dateObj.getMilliseconds());
};

const toDateOnly = (dateObj) => new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());

const isSameDate = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const diffCalendarMonths = (start, end) => {
    const startDate = toDateOnly(start);
    const endDate = toDateOnly(end);
    let months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + endDate.getMonth() - startDate.getMonth();
    if (months <= 0) return 0;
    if (addCalendarMonths(startDate, months) > endDate) months--;
    return Math.max(months, 0);
};

const addToYMDString = (base, { days = 0, months = 0, years = 0 } = {}) => {
    const ymd = ymdFromAny(base);
    if (!ymd) return '';
    const dash = ymd.dash;
    let dt = dateFromYmd(ymd);
    if (!dt) return '';

    if (days) dt.setDate(dt.getDate() + days);
    if (months || years) dt = addCalendarMonths(dt, months + years * 12);

    return fmtYMD(dt, dash);
};
const initialData = {
    contract_no: '', // 계약번호
    vehicle_no: '', // 차량번호
    vehicle_model_name: '', // 차량모델
    vehicle_group_id: 0, // 차량그룹
    fuel_type: '', // 유류종류
    pickup_date: formattedDate, // 배차일자
    pickup_time: currentTime, // 배차시간
    return_expected_date: formattedTomorrow, // 반납(예정) 일자
    return_expected_time: currentTime, // 반납(예정) 시간
    garage_id: 0, // 지점
    pickup_delivery_id: 0, // 딜리버리(대여)
    pickup_delivery_address: '', // 딜리버리(대여) 주소
    pickup_delivery_address_detail: '', // 딜리버리(대여) 상세주소
    return_expected_delivery_id: 0, // 딜리버리(반납)
    return_delivery_address: '', // 딜리버리(반납) 주소
    return_delivery_address_detail: '', // 딜리버리(반납) 상세주소
    pickup_fuel_gauge: 0, // 대여시유류량
    pickup_mileage: 0, // 대여시주행거리
    return_fuel_gauge: 0, // 반납시유류량
    return_mileage: 0 // 반납시주행거리
};

const data = reactive({ ...initialData });

const sessionRef = store.ensure(sessionKey);

const commonSyncKeys = computed(() => Object.keys(data).filter((k) => Object.prototype.hasOwnProperty.call(sessionRef, k)));
const pickKeys = (src, keys) =>
    keys.reduce((o, k) => {
        if (k in src) o[k] = src[k];
        return o;
    }, {});

//store.update(sessionKey, pickKeys(data, commonSyncKeys.value));

let pushing = false;
let pulling = false;

const requiredData = ['vehicle_no', 'pickup_date', 'return_expected_date', 'garage_id'];

const requiredKeys = computed(() => {
    const res = props.requiredList;
    if (Array.isArray(res) && res.length) return res;
    if (res && typeof res === 'object' && Object.keys(res).length) {
        return Object.keys(res).filter((key) => !!res[key]);
    }
    return requiredData;
});

const requiredKeySet = computed(() => new Set(requiredKeys.value));
const isEffectivelyRequired = (key) => requiredKeySet.value.has(key) && !isFieldReadonly(key) && !isHidden(key);

const initialValidMessage = {
    contract_no: '',
    vehicle_no: '',
    vehicle_model_name: '',
    vehicle_group_id: '',
    fuel_type: '',
    pickup_date: '',
    pickup_time: '',
    return_expected_date: '',
    return_expected_time: '',
    pickup_delivery_address_detail: '',
    pickup_delivery_address: '',
    return_delivery_address: '',
    return_delivery_address_detail: '',
    garage_id: '',
    pickup_fuel_gauge: '',
    pickup_mileage: '',
    return_fuel_gauge: '',
    return_mileage: ''
};
const validationMessage = reactive({ ...initialValidMessage });

// const readonlyKeySet = computed(() => {
//     const res = props.readonlyList;
//     if (Array.isArray(res) && res.length) return new Set(res);
//     if (res && typeof res === 'object' && Object.keys(res).length) {
//         return new Set(Object.keys(res).filter((key) => !!res[key]));
//     }
//     return new Set();
// });

// const isFieldReadonly = (key) => readonlyKeySet.value.has(key);

const propsReadonlyEnforced = computed(() => {
    const res = props.readonlyList;
    if (Array.isArray(res)) return new Set(res);
    if (res && typeof res === 'object') return new Set(Object.keys(res).filter((k) => !!res[k]));
    return new Set();
});

const propsReadonlyControlled = computed(() => {
    const res = props.readonlyList;
    if (Array.isArray(res)) return new Set(res);
    if (res && typeof res === 'object') return new Set(Object.keys(res));
    return new Set();
});

const allReadonly = ref(false);
const setAllReadonly = (val = true) => {
    allReadonly.value = !!val;
};

const internalReadonlyKeys = computed(() => {
    const set = new Set();
    if (allReadonly.value) Object.keys(initialData).forEach((k) => set.add(k));
    return set;
});

const finalReadonlySet = computed(() => {
    const result = new Set(propsReadonlyEnforced.value);
    internalReadonlyKeys.value.forEach((k) => {
        if (!propsReadonlyControlled.value.has(k)) result.add(k);
    });
    return result;
});

const isFieldReadonly = (key) => finalReadonlySet.value.has(key);

const hideKeySet = computed(() => {
    const res = props.hideList;
    if (Array.isArray(res) && res.length) return new Set(res);
    if (res && typeof res === 'object' && Object.keys(res).length) {
        return new Set(Object.keys(res).filter((key) => !!res[key]));
    }
    return new Set();
});
const isHidden = (key) => hideKeySet.value.has(key);

const resetData = () => {
    Object.assign(data, initialData);
    Object.assign(validationMessage, initialValidMessage);
    setDefaultReturnDate();
    store.update(sessionKey, pickKeys(data, commonSyncKeys.value));
};

const calculateDate = (base, delta) => addToYMDString(base, delta);

const toDate = (v) => {
    const ymd = ymdFromAny(v);
    if (!ymd) return new Date('Invalid');
    const dt = dateFromYmd(ymd);
    return dt ?? new Date('Invalid');
};

const normalizeDateStr = (v) => {
    const ymd = ymdFromAny(v);
    if (!ymd) return '';
    const dt = dateFromYmd(ymd);
    if (!dt) return '';
    return fmtYMD(dt, true); // 항상 'YYYY-MM-DD' 로 정규화
};

const minReturnDate = computed(() => {
    const baseDate = data.pickup_date;
    switch (props.contractType) {
        case 'SHORT':
        case 'INSU':
            return normalizeDateStr(baseDate);
        case 'MID':
            return calculateDate(baseDate, { months: 1 });
        case 'LONG':
            return calculateDate(baseDate, { years: 1 });
        default:
            return '';
    }
});

const maxReturnDate = computed(() => {
    const base = data.pickup_date;
    switch (props.contractType) {
        case 'INSU':
            return calculateDate(base, { days: 30 });
        default:
            return '';
    }
});

const setDefaultReturnDate = () => {
    const type = props.contractType;
    const pickupDate = data.pickup_date;
    let defaultReturnDate = '';

    switch (type) {
        case 'SHORT':
            defaultReturnDate = calculateDate(pickupDate, { days: 1 });
            break;
        case 'INSU':
            defaultReturnDate = calculateDate(pickupDate, { days: 7 });
            break;
        case 'MID':
            defaultReturnDate = calculateDate(pickupDate, { months: 1 });
            break;
        case 'LONG':
            defaultReturnDate = calculateDate(pickupDate, { months: 12 });
            break;
    }

    data.return_expected_date = defaultReturnDate;
    data.return_expected_time = data.pickup_time;
};

const setPickupDate = (value) => {
    data.pickup_date = value;
    if (!initializing.value) setDefaultReturnDate();
};

const setPickupTime = (value) => {
    data.pickup_time = value;
    if (!initializing.value) data.return_expected_time = value;
};

const setReturnExpectedDate = (value) => {
    data.return_expected_date = value;
};

const getData = () => {
    const payload = {};
    for (const key of allowedKeys) {
        if (!isHidden(key)) payload[key] = data[key];
    }
    return payload;
};

const allowedKeys = Object.keys(initialData);
const setVehicleData = (newVal) => {
    if (!newVal) return;
    const payload = newVal.contract_data ?? newVal;

    Object.entries(payload).forEach(([key, val]) => {
        if (allowedKeys.includes(key)) {
            data[key] = val;
        }
    });

    nextTick(() => {
        data.vehicle_no = payload.vehicle_no ?? sessionRef.vehicle_no ?? ''; // if ('vehicle_no' in payload) data.vehicle_no = payload.vehicle_no ?? '';
    });

    console.log('들어온 vehicle data: ', data);
};

const checkRequired = () => {
    Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    let result = true;

    const keysToCheck = new Set(requiredKeys.value.filter((key) => allowedKeys.includes(key) && isEffectivelyRequired(key)));

    if (isPickupAddressRequired.value && allowedKeys.includes('pickup_delivery_address') && !isHidden('pickup_delivery_address')) {
        keysToCheck.add('pickup_delivery_address');
    }
    if (isReturnAddressRequired.value && allowedKeys.includes('return_delivery_address') && !isHidden('return_delivery_address')) {
        keysToCheck.add('return_delivery_address');
    }

    for (const key of keysToCheck) {
        const val = data[key];
        const isEmpty = val === '' || val === null || val === undefined;
        const isIdInvalid = key.endsWith('_id') && (isEmpty || Number(val) === 0);
        if (isEmpty || isIdInvalid) {
            if (key in validationMessage) validationMessage[key] = '필수입력입니다.';
            result = false;
        }
    }
    return result;
};

const setErrorMessage = (payload, reset = false) => {
    if (!payload) return;

    if (reset) {
        Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    }

    const collectPairs = (node, out = []) => {
        if (node == null) return out;

        if (Array.isArray(node)) {
            node.forEach((item) => collectPairs(item, out));
            return out;
        }

        if (typeof node === 'object') {
            for (const [k, v] of Object.entries(node)) {
                if (v && typeof v === 'object' && !Array.isArray(v)) {
                    collectPairs(v, out);
                } else {
                    out.push([k, v]);
                }
            }
            return out;
        }

        return out;
    };

    const pairs = collectPairs(payload);

    for (const [rawKey, rawVal] of pairs) {
        let key = String(rawKey).replace(/^contract_data\./, '');
        if (!(key in validationMessage)) continue;

        let msg = rawVal;
        if (Array.isArray(msg)) msg = msg.filter(Boolean).join(', ');
        if (msg == null) msg = '';

        validationMessage[key] = String(msg);
    }
};

const updateInputValue = (val) => {
    store.update(sessionKey, {
        vehicle_id: val.vehicle_id ? Number(val.vehicle_id) : 0
    });
    data.vehicle_no = val.value || '';
    data.vehicle_model_name = val.model || '';
    data.fuel_type = val.fuel_type || '';
    data.vehicle_group_id = val.vehicle_group_id ? Number(val.vehicle_group_id) : 0;
    emit('update', getData());
};

const isValidTime = (t) => /^\d{2}:\d{2}$/.test(t);

const plusReturnDateTime = (days) => {
    const pick = toDate(data.pickup_date);
    if (isNaN(pick)) return;

    const base = toDate(data.return_expected_date || data.pickup_date);
    if (isNaN(base)) return;

    base.setDate(base.getDate() + days);

    const cap = toDate(maxReturnDate.value);
    const next = !isNaN(cap) && base > cap ? cap : base;

    data.return_expected_date = normalizeDateStr(next);
    data.return_expected_time = data.pickup_time;
};

const plusContractMonth = (monthsToAdd) => {
    let minMonths = 0;

    switch (props.contractType) {
        case 'MID':
            minMonths = 1;
            break;
        case 'LONG':
            minMonths = 12;
            break;
        default:
            return;
    }

    const months = Math.max(monthsToAdd, minMonths);

    const newReturnDate = calculateDate(data.pickup_date, { months });

    data.return_expected_date = normalizeDateStr(newReturnDate);
    data.return_expected_time = data.pickup_time;
};

const calculateRentalDuration = () => {
    const { pickup_date, return_expected_date, pickup_time, return_expected_time } = data;
    const p = normalizeDateStr(pickup_date);
    const r = normalizeDateStr(return_expected_date);

    if (!p || !r) {
        rentalDuration.value = '';
        rentalMonth.value = 0;
        return;
    }

    const st = new Date(`${p}T${isValidTime(pickup_time) ? pickup_time : '00:00'}`);
    const en = new Date(`${r}T${isValidTime(return_expected_time) ? return_expected_time : '00:00'}`);

    if (isNaN(st) || isNaN(en) || en < st) {
        rentalDuration.value = '';
        rentalMonth.value = 0;
        return;
    }

    const months = diffCalendarMonths(st, en);
    let tmp = addCalendarMonths(st, months);
    if (isSameDate(tmp, en) && tmp > en) tmp = new Date(en);

    const ms = en - tmp;
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);

    rentalMonth.value = en > st ? Math.max(months, 1) : 0;

    const parts = [];
    if (months) parts.push(`${months}개월`);
    if (days) parts.push(`${days}일`);
    if (hours) parts.push(`${hours}시간`);
    rentalDuration.value = parts.join(' ') || '';
};

const clearDateTime = () => {
    const today = new Date();
    const currentTime = today.toTimeString().slice(0, 5);
    data.pickup_date = formatToYyyyMmDd(today);
    data.pickup_time = currentTime;
    setDefaultReturnDate();
};

const isPickupAddressReadonly = computed(() => {
    const val = data.pickup_delivery_id;
    const isEmpty = val === 0 || val === null || val === undefined || val === '';
    validationMessage['pickup_delivery_address'] = '';
    return isEmpty;
});

const isReturnAddressReadonly = computed(() => {
    const val = data.return_expected_delivery_id;
    const isEmpty = val === 0 || val === null || val === undefined || val === '';
    validationMessage['return_delivery_address'] = '';
    return isEmpty;
});

const isDateEditReadonly = computed(() => isFieldReadonly('pickup_date') || isFieldReadonly('return_expected_date'));

const isPickupAddressRequired = computed(() => {
    return !isPickupAddressReadonly.value && !isFieldReadonly('pickup_delivery_address');
});

const isReturnAddressRequired = computed(() => {
    return !isReturnAddressReadonly.value && !isFieldReadonly('return_delivery_address');
});

function handleAddressCompleted(inputId, { address, detailAddress }) {
    data[`${inputId}_address`] = address;
    data[`${inputId}_address_detail`] = detailAddress;
}

const initializing = ref(true);
onMounted(() => {
    //resetData();
    if (props.vehicleData && Object.keys(props.vehicleData).length) {
        setVehicleData(props.vehicleData);
    } else {
        data.vehicle_no = sessionRef.vehicle_no ?? '';
    }
    nextTick(() => {
        initializing.value = false;
    });
});

watch(
    () => props.vehicleData,
    (newVal) => {
        if (!newVal) return;
        const payload = newVal.contract_data ?? newVal;
        Object.entries(payload).forEach(([key, val]) => {
            if (allowedKeys.includes(key)) data[key] = val;
        });
    },
    { immediate: false, deep: true, flush: 'post' }
);

watch(
    () => commonSyncKeys.value.map((k) => data[k]),
    () => {
        if (pulling || initializing.value) return;
        pushing = true;
        store.update(sessionKey, pickKeys(data, commonSyncKeys.value));
        pushing = false;
    },
    { deep: false, immediate: false }
);

watch(
    () => commonSyncKeys.value.map((k) => sessionRef[k]),
    () => {
        if (pushing) return;
        pulling = true;
        commonSyncKeys.value.forEach((k) => {
            if (sessionRef[k] !== undefined && data[k] !== sessionRef[k]) {
                data[k] = sessionRef[k];
            }
        });
        pulling = false;
    },
    { deep: false, immediate: true }
);

watch(
    () => props.validMsg,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') {
            console.error('Invalid Error data');
            return;
        }
        setErrorMessage(newVal, true);
        // Object.keys(newVal).forEach((key) => {
        //     if (key.startsWith('contract_data.')) {
        //         const pureKey = key.replace('contract_data.', '');
        //         if (pureKey in validationMessage) {
        //             validationMessage[pureKey] = newVal[key];
        //         }
        //     } else {
        //         if (key in validationMessage) {
        //             validationMessage[key] = newVal[key];
        //         }
        //     }
        // });
    },
    { immediate: true }
);

watch(
    () => props.hideList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isHidden(key)) validationMessage[key] = '';
        });
    },
    { immediate: false, deep: false }
);

watch(
    () => [props.contractType, data.pickup_date],
    () => {
        if (initializing.value) return;
        setDefaultReturnDate();
    },
    { immediate: true }
);
// 일시 변경 시 기간 표시 갱신
watch(
    () => [data.pickup_date, data.return_expected_date, data.pickup_time, data.return_expected_time],
    () => calculateRentalDuration(),
    { immediate: true }
);

watch(
    rentalMonth,
    (month) => {
        emit('update:rentalMonth', month);
    },
    { immediate: true }
);

// 딜리버리 선택 초기화
watch(
    () => data.pickup_delivery_id,
    (newId) => {
        if (newId === 0 || newId === '') {
            data.pickup_delivery_address = '';
            data.pickup_delivery_address_detail = '';
        }
    }
);
watch(
    () => data.return_expected_delivery_id,
    (newId) => {
        if (newId === 0 || newId === '') {
            data.return_delivery_address = '';
            data.return_delivery_address_detail = '';
        }
    }
);

defineExpose({
    getData,
    resetData,
    setVehicleData,
    checkRequired,
    setAllReadonly,
    setErrorMessage
});
</script>

<template>
    <div class="section-box base-text">
        <h2 class="section-title py-4">차량 정보</h2>
        <div class="section-body">
            <div class="section-left">
                <div class="section-row items-center">
                    <div class="input-box w-[40%]">
                        <DatePickerWithRegistComponent2
                            :required="isEffectivelyRequired('pickup_date')"
                            inputId="pickup_date"
                            :modelValue="data.pickup_date"
                            @update:modelValue="setPickupDate"
                            :readonly="isFieldReadonly('pickup_date')"
                            :errmsg="validationMessage.pickup_date"
                            label="배차일시"
                        />
                    </div>
                    <div class="input-box w-[30%]">
                        <timeonly inputId="pickup_time" :placeholder="'00:00'" :modelValue="data.pickup_time" @update:modelValue="setPickupTime" :errmsg="validationMessage.pickup_time" :readonly="isFieldReadonly('pickup_time')" />
                    </div>
                    <div class="flex w-[30%] justify-between text-[0.9rem] mt-[2.2rem]">
                        <button class="rounded-[0.4rem] text-[#0038ea]" @click="clearDateTime()">
                            <i v-if="!isDateEditReadonly" class="pi pi-undo"></i>
                        </button>
                        <p class="select-none">{{ rentalDuration === '' ? '배차기간을 선택하세요.' : rentalDuration }}</p>
                    </div>
                </div>
                <div class="section-row items-center">
                    <div class="input-box w-[40%]">
                        <DatePickerWithRegistComponent2
                            :required="isEffectivelyRequired('return_expected_date')"
                            inputId="return_expected_date"
                            :modelValue="data.return_expected_date"
                            @update:modelValue="setReturnExpectedDate"
                            :errmsg="validationMessage.return_expected_date"
                            label="반납일시"
                            :readonly="isFieldReadonly('return_expected_date')"
                        />
                    </div>
                    <div class="input-box w-[30%]">
                        <timeonly inputId="return_expected_time" :placeholder="'00:00'" v-model="data.return_expected_time" :errmsg="validationMessage.return_expected_time" :readonly="isFieldReadonly('return_expected_time')" />
                    </div>

                    <div v-if="(contractType === 'SHORT' || contractType === 'INSU') && !isDateEditReadonly" class="flex items-center mt-8 w-[30%] gap-2 text-sm">
                        <button @click="plusReturnDateTime(1)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 1일</button>
                        <button @click="plusReturnDateTime(7)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 7일</button>
                        <button @click="plusReturnDateTime(10)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 10일</button>
                    </div>
                    <div v-else-if="!isDateEditReadonly" class="flex items-center mt-8 w-[30%] gap-2 text-sm">
                        <button @click="plusContractMonth(1)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 1개월</button>
                        <button @click="plusContractMonth(6)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 6개월</button>
                        <button @click="plusContractMonth(12)" class="flex-1 h-[2.5rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">+ 1년</button>
                    </div>
                    <div v-else class="flex items-center mt-8 w-[30%] gap-2 text-sm"></div>
                </div>
                <!-- // 배차일시/반납일시 -->
                <div class="section-row">
                    <div class="input-box w-[40%]">
                        <vehicle
                            inputId="vehicle_no"
                            :required="isEffectivelyRequired('vehicle_no')"
                            :placeholder="'차량을 선택하세요'"
                            v-model="data.vehicle_no"
                            :errmsg="validationMessage.vehicle_no"
                            label="차량번호"
                            :readonly="isFieldReadonly('vehicle_no')"
                            @input="updateInputValue($event)"
                            :selectedDate="selectedDate"
                        />
                    </div>
                    <div class="input-box w-[30%]">
                        <inputbox inputId="vehicle_model_name" v-model="data.vehicle_model_name" :errmsg="validationMessage.vehicle_model_name" :readonly="true" label="모델" />
                    </div>
                    <div class="input-box w-[30%]">
                        <inputbox inputId="fuel_type" v-model="data.fuel_type" :errmsg="validationMessage.fuel_type" :readonly="true" label="유종" />
                    </div>
                </div>
                <!-- // 차량 -->
                <div v-if="!isHidden('pickup_fuel_gauge') || !isHidden('pickup_mileage')" class="section-row items-center">
                    <div class="input-box w-[39.5%]">
                        <inputbox
                            inputId="pickup_fuel_gauge"
                            :number="'n'"
                            :placeholder="'배차 유류량을 입력하세요.'"
                            :unit="'%'"
                            v-model="data.pickup_fuel_gauge"
                            :errmsg="validationMessage.pickup_fuel_gauge"
                            label="배차 유류량"
                            :maxlength="3"
                            :readonly="isFieldReadonly('pickup_fuel_gauge')"
                        />
                    </div>
                    <div class="input-box w-[60%]">
                        <inputbox
                            inputId="pickup_mileage"
                            :number="'n'"
                            :placeholder="'배차 시 KM를 입력하세요.'"
                            :unit="'KM'"
                            v-model="data.pickup_mileage"
                            :errmsg="validationMessage.pickup_mileage"
                            label="배차 시 KM"
                            :maxlength="7"
                            :readonly="isFieldReadonly('pickup_mileage')"
                        />
                    </div>
                </div>
            </div>
            <div class="section-right">
                <div class="section-row">
                    <div class="input-box w-[100%]">
                        <garage
                            inputId="garage_id"
                            :required="isEffectivelyRequired('garage_id')"
                            :placeholder="'지점을 선택하세요'"
                            v-model="data.garage_id"
                            :readonly="isFieldReadonly('garage_id')"
                            :errmsg="validationMessage.garage_id"
                            label="지점"
                        />
                    </div>
                </div>
                <div class="section-row">
                    <div class="input-box w-[30%]">
                        <delivery inputId="pickup_delivery_id" :placeholder="'행정구역을 선택하세요.'" v-model="data.pickup_delivery_id" :readonly="isFieldReadonly('pickup_delivery_id')" :padding="'0.5rem'" label="딜리버리(인수)" />
                    </div>
                    <div class="input-box w-[50%]">
                        <addresscomponent
                            inputId="pickup_delivery_address"
                            :readonly="isFieldReadonly('pickup_delivery_address') || isPickupAddressReadonly"
                            label="인수장소"
                            v-model="data.pickup_delivery_address"
                            :errmsg="validationMessage.pickup_delivery_address"
                            :required="isPickupAddressRequired"
                            @addressCompleted="(payload) => handleAddressCompleted('pickup_delivery', payload)"
                        />
                    </div>
                    <div class="input-box w-[40%]">
                        <inputbox
                            inputId="pickup_delivery_address_detail"
                            :placeholder="'상세주소를 입력하세요.'"
                            v-model="data.pickup_delivery_address_detail"
                            :errmsg="validationMessage.pickup_delivery_address_detail"
                            :readonly="isFieldReadonly('pickup_delivery_address_detail') || isPickupAddressReadonly"
                        />
                    </div>
                </div>
                <div class="section-row">
                    <div class="input-box w-[30%]">
                        <delivery inputId="return_expected_delivery_id" :placeholder="'행정구역을 선택하세요.'" v-model="data.return_expected_delivery_id" :readonly="isFieldReadonly('return_expected_delivery_id')" label="딜리버리(반납)" />
                    </div>
                    <div class="input-box w-[50%]">
                        <addresscomponent
                            inputId="return_delivery_address"
                            :readonly="isFieldReadonly('return_delivery_address') || isReturnAddressReadonly"
                            label="반납장소"
                            v-model="data.return_delivery_address"
                            :errmsg="validationMessage.return_delivery_address"
                            :required="isReturnAddressRequired"
                            @addressCompleted="(payload) => handleAddressCompleted('return_delivery', payload)"
                        />
                    </div>
                    <div class="input-box w-[40%]">
                        <inputbox
                            inputId="return_delivery_address_detail"
                            :placeholder="'상세주소를 입력하세요.'"
                            v-model="data.return_delivery_address_detail"
                            :errmsg="validationMessage.return_delivery_address_detail"
                            :readonly="isFieldReadonly('return_delivery_address_detail') || isReturnAddressReadonly"
                        />
                    </div>
                </div>
                <div v-if="!isHidden('return_fuel_gauge') || !isHidden('return_mileage')" class="section-row items-center">
                    <div class="input-box w-[24.7%]">
                        <inputbox
                            inputId="return_fuel_gauge"
                            :number="'n'"
                            :placeholder="'반납 유류량을 입력하세요.'"
                            :unit="'%'"
                            v-model="data.return_fuel_gauge"
                            :errmsg="validationMessage.return_fuel_gauge"
                            label="반납 유류량"
                            :maxlength="3"
                            :readonly="isFieldReadonly('return_fuel_gauge')"
                        />
                    </div>
                    <div class="input-box w-[75.3%]">
                        <inputbox
                            inputId="return_mileage"
                            :number="'n'"
                            :placeholder="'반납 시 KM를 입력하세요.'"
                            :unit="'KM'"
                            v-model="data.return_mileage"
                            :errmsg="validationMessage.return_mileage"
                            label="반납 시 KM"
                            :maxlength="7"
                            :readonly="isFieldReadonly('return_mileage')"
                            :required="isEffectivelyRequired('return_mileage')"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style src="@/assets/css/form-common.css"></style>
